import { IStub } from "redux/reducers/models/ticket";

export type TicketRedemptionQuantityMap = Record<number, number>;

export interface IRedemptionVariant {
  variant_id: number;
  quantity: number;
}

type RedemptionProductWithPivot = {
  pivot?: { variant_id?: number; quantity?: number };
};

export function normalizeRedemptionItems(
  items?: Array<RedemptionProductWithPivot> | Record<string, RedemptionProductWithPivot> | null,
): RedemptionProductWithPivot[] {
  if (!items) {
    return [];
  }

  return Array.isArray(items) ? items : Object.values(items);
}

export function stubHasRedemptionProducts(stub?: IStub | null): boolean {
  if (!stub?.redemption_product) {
    return false;
  }

  const { green_fee, power_cart, items } = stub.redemption_product;

  return Boolean(
    green_fee?.pivot?.variant_id ||
      power_cart?.pivot?.variant_id ||
      normalizeRedemptionItems(items).some(item => item?.pivot?.variant_id),
  );
}

export function buildRedemptionItemVariantsFromStub(stub: IStub, useCount = 1): IRedemptionVariant[] {
  const uses = Math.max(1, useCount);
  const variants: IRedemptionVariant[] = [];

  for (const item of normalizeRedemptionItems(stub?.redemption_product?.items)) {
    const variantId = item.pivot?.variant_id;
    if (!variantId) {
      continue;
    }

    variants.push({
      variant_id: variantId,
      quantity: (item.pivot?.quantity ?? 1) * uses,
    });
  }

  return variants;
}

export function buildRedemptionVariantsFromStub(stub: IStub, useCount = 1): IRedemptionVariant[] {
  const uses = Math.max(1, useCount);
  const variants: IRedemptionVariant[] = [];

  const addVariant = (product?: RedemptionProductWithPivot | null) => {
    const variantId = product?.pivot?.variant_id;
    if (!variantId) {
      return;
    }

    variants.push({
      variant_id: variantId,
      quantity: (product?.pivot?.quantity ?? 1) * uses,
    });
  };

  addVariant(stub?.redemption_product?.green_fee);
  addVariant(stub?.redemption_product?.power_cart);
  normalizeRedemptionItems(stub?.redemption_product?.items).forEach(addVariant);

  return variants;
}

export function getRedemptionItemSlots(stub?: IStub | null): {
  greenFeeItem: RedemptionProductWithPivot | null;
  powerCartItem: RedemptionProductWithPivot | null;
} {
  const items = normalizeRedemptionItems(stub?.redemption_product?.items);

  return {
    greenFeeItem: items[0]?.pivot?.variant_id ? items[0] : null,
    powerCartItem: items[1]?.pivot?.variant_id ? items[1] : null,
  };
}

export function stubHasItemRedemptionProducts(stub?: IStub | null): boolean {
  return normalizeRedemptionItems(stub?.redemption_product?.items).some(item => item?.pivot?.variant_id);
}

export function getParticipantRedemptionAssignmentCount(
  stub: IStub,
  quantities: TicketRedemptionQuantityMap,
  participantCount: number,
): number {
  const redemptionUseCount = getRedemptionUseCount(stub, quantities, participantCount);

  if (stubHasFeeRedemptionProducts(stub)) {
    return redemptionUseCount;
  }

  return Math.min(redemptionUseCount, Math.max(participantCount, 0));
}

export function getAssignmentGreenFeeVariantId(stub?: IStub | null): number | null {
  const greenFeeVariantId = stub?.redemption_product?.green_fee?.pivot?.variant_id;
  if (greenFeeVariantId) {
    return greenFeeVariantId;
  }

  return getRedemptionItemSlots(stub).greenFeeItem?.pivot?.variant_id ?? null;
}

export function getAssignmentPowerCartVariantId(stub?: IStub | null): number | null {
  const powerCartVariantId = stub?.redemption_product?.power_cart?.pivot?.variant_id;
  if (powerCartVariantId) {
    return powerCartVariantId;
  }

  return getRedemptionItemSlots(stub).powerCartItem?.pivot?.variant_id ?? null;
}

export function getAssignmentRedemptionQuantities(
  stub?: IStub | null,
  perUseAssignment = false,
): { greenFeeQuantity: number; powerCartQuantity: number } {
  const greenFeeProduct = stub?.redemption_product?.green_fee;
  const powerCartProduct = stub?.redemption_product?.power_cart;
  const { greenFeeItem, powerCartItem } = getRedemptionItemSlots(stub);

  if (perUseAssignment) {
    return {
      greenFeeQuantity: getAssignmentGreenFeeVariantId(stub) ? 1 : 0,
      powerCartQuantity: getAssignmentPowerCartVariantId(stub) ? 1 : 0,
    };
  }

  return {
    greenFeeQuantity: Number(
      greenFeeProduct?.pivot?.quantity ?? greenFeeItem?.pivot?.quantity ?? 0,
    ),
    powerCartQuantity: Number(
      powerCartProduct?.pivot?.quantity ?? powerCartItem?.pivot?.quantity ?? 0,
    ),
  };
}

export function stubHasFeeRedemptionProducts(stub?: IStub | null): boolean {
  if (!stub?.redemption_product) {
    return false;
  }

  const greenFeeQuantity = Number(stub.redemption_product.green_fee?.pivot?.quantity);
  const powerCartQuantity = Number(stub.redemption_product.power_cart?.pivot?.quantity);

  return (
    (Boolean(stub.redemption_product.green_fee?.pivot?.variant_id) && greenFeeQuantity >= 1) ||
    (Boolean(stub.redemption_product.power_cart?.pivot?.variant_id) && powerCartQuantity >= 1)
  );
}

export function getRedemptionUseCount(
  stub: IStub,
  quantities: TicketRedemptionQuantityMap,
  participantCount?: number,
): number {
  const selectedQuantity = getTicketRedemptionQuantity(stub.id, quantities);

  if (!isMultiUseTicket(stub)) {
    return 1;
  }

  const remaining = getMaxRedemptionQuantity(stub);
  let maxQuantity = Math.min(selectedQuantity, remaining);

  if (stubHasFeeRedemptionProducts(stub) && participantCount !== undefined) {
    maxQuantity = Math.min(maxQuantity, participantCount);
  }

  return Math.max(1, maxQuantity);
}

export function isMultiUseTicket(stub?: IStub | null): boolean {
  return stub?.type === "multi_use" || stub?.ticket?.multi_use === true;
}

export function getTicketRedemptionQuantity(
  stubId: number,
  quantities: TicketRedemptionQuantityMap,
  fallback = 1,
): number {
  const quantity = quantities[stubId] ?? fallback;
  return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : fallback;
}

export function getMaxRedemptionQuantity(stub?: IStub | null, participantLimit?: number): number {
  const remaining = Number(stub?.redemptions_remaining ?? 1);

  if (participantLimit !== undefined && participantLimit > 0) {
    return Math.max(1, Math.min(remaining, participantLimit));
  }

  return Math.max(1, remaining);
}

export function expandTicketStubIds(
  stubs: IStub[],
  quantities: TicketRedemptionQuantityMap,
): number[] {
  const stubIds: number[] = [];

  for (const stub of stubs) {
    const quantity = isMultiUseTicket(stub) ? getTicketRedemptionQuantity(stub.id, quantities) : 1;
    for (let i = 0; i < quantity; i++) {
      stubIds.push(stub.id);
    }
  }

  return stubIds;
}

export function countPendingTicketStubIds(stubIds: number[]): TicketRedemptionQuantityMap {
  return stubIds.reduce<TicketRedemptionQuantityMap>((counts, stubId) => {
    counts[stubId] = (counts[stubId] ?? 0) + 1;
    return counts;
  }, {});
}

export function buildRedemptionVariantsFromRedeemedStubs(stubs: IStub[]): IRedemptionVariant[] {
  const variants: IRedemptionVariant[] = [];

  for (const stub of stubs) {
    variants.push(...buildRedemptionVariantsFromStub(stub, 1));
  }

  return variants;
}

type TicketFeeRedemptionSlot = {
  status?: string | null;
  booking?: { id?: number | null; booking_fee_order_id?: number | null } | null;
  booking_participant?: {
    order_financial_status?: string | null;
    order_id?: number | null;
    pre_paid?: boolean | null;
    green_fee_paid?: boolean | null;
  } | null;
};

/**
 * Whether a tee-sheet slot can receive ticket green-fee / power-cart products.
 *
 * Do not treat order_financial_status === "paid" alone as ineligible — early
 * booking-fee checkouts attach a paid order without collecting green fees
 * (pre_paid / green_fee_paid remain false).
 */
export function isSlotEligibleForTicketFeeRedemption(
  slot: TicketFeeRedemptionSlot | null | undefined,
  selectedBookingId?: number | null,
): boolean {
  if (!slot || slot.status !== "booked") {
    return false;
  }

  if (selectedBookingId != null && slot.booking?.id !== selectedBookingId) {
    return false;
  }

  const participant = slot.booking_participant;
  if (!participant) {
    return false;
  }

  // Prepaid online green fees, or green fees already collected at the course
  if (participant.pre_paid || participant.green_fee_paid) {
    return false;
  }

  return true;
}
